import React from 'react';
import { Clock } from 'lucide-react';

/**
 * TimeProgressBar component - Displays time tracking progress for a task
 * 
 * @param {number} actualHours - Actual hours spent on the task
 * @param {number} estimatedHours - Estimated hours for the task
 * @param {boolean} showLabel - Whether to show the time label
 * @param {boolean} isActive - Whether there's an active timer for this task
 * @param {string} size - Size of the progress bar: 'sm', 'md', or 'lg'
 */
const TimeProgressBar = ({ 
  actualHours = 0, 
  estimatedHours = 0, 
  showLabel = true,
  isActive = false,
  size = 'md'
}) => {
  // Calculate completion percentage
  const completionPercentage = estimatedHours > 0 
    ? Math.min(100, Math.round((actualHours / estimatedHours) * 100)) 
    : 0;
  
  // Determine color based on progress
  const getProgressColor = () => {
    if (completionPercentage > 90) return 'bg-red-500 dark:bg-red-600'; // Over budget
    if (completionPercentage > 75) return 'bg-yellow-500 dark:bg-yellow-600'; // Near budget
    return 'bg-green-500 dark:bg-green-600'; // Under budget
  };
  
  // Size classes
  const sizeClasses = {
    sm: 'h-1 text-xs',
    md: 'h-2 text-sm',
    lg: 'h-3 text-base'
  };
  
  // Animate the progress bar if there's an active timer
  const animationClass = isActive ? 'animate-pulse' : '';
  
  return (
    <div className={`w-full ${showLabel ? 'mb-1' : ''}`}>
      {showLabel && (
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center">
            <Clock className={`w-3 h-3 mr-1 ${isActive ? 'text-green-500 animate-pulse' : 'text-grayteck-500 dark:text-grayteck-400'}`} />
            <span className={`text-xs ${sizeClasses[size] || 'text-sm'} text-grayteck-700 dark:text-grayteck-300`}>
              {actualHours.toFixed(1)}h {estimatedHours > 0 ? `/ ${estimatedHours}h` : ''}
              {isActive && <span className="text-green-500 ml-1">(Active)</span>}
            </span>
          </div>
          {estimatedHours > 0 && (
            <span className={`text-xs ${completionPercentage > 90 ? 'text-red-600 dark:text-red-400' : 'text-grayteck-500 dark:text-grayteck-400'}`}>
              {completionPercentage}%
            </span>
          )}
        </div>
      )}
      
      <div className={`w-full ${sizeClasses[size] || 'h-2'} bg-grayteck-200 dark:bg-grayteck-700 rounded-full overflow-hidden`}>
        <div 
          className={`${getProgressColor()} ${sizeClasses[size] || 'h-2'} rounded-full ${animationClass}`}
          style={{ width: `${estimatedHours > 0 ? completionPercentage : 0}%` }}
        />
      </div>
    </div>
  );
};

export default TimeProgressBar;